'use client';

import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn, formatUSD, formatTimeAgo } from '@/lib/utils';
import { AvatarTooltip, type IdentityInfo } from '@/components/shared/avatar-tooltip';
import type { V2GridSeatEvent } from '@/lib/types/v2';
import { formatUnits } from 'viem';
import { getTaxTokenConfig, formatTaxAmount, takeoverToUsd } from '@/lib/drawer-utils';

function useUserIdentities(events: V2GridSeatEvent[]) {
  const addresses = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) {
      if (e.user && !e.userIdentity) set.add(e.user.toLowerCase());
      if (e.oldHolder && !e.oldHolderIdentity) set.add(e.oldHolder.toLowerCase());
    }
    return Array.from(set);
  }, [events]);

  const { data } = useQuery({
    queryKey: ['batch-identities', addresses.sort().join(',')],
    queryFn: async () => {
      if (addresses.length === 0) return {} as Record<string, IdentityInfo>;
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ addresses }),
      });
      if (!res.ok) return {} as Record<string, IdentityInfo>;
      const json = await res.json();
      return (json.data ?? {}) as Record<string, IdentityInfo>;
    },
    enabled: addresses.length > 0,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });

  return data ?? ({} as Record<string, IdentityInfo>);
}

type Timeframe = '24h' | '7d' | '1m' | 'all';
const TIMEFRAME_OPTIONS: { value: Timeframe; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7D' },
  { value: '1m', label: '1M' },
  { value: 'all', label: 'All' },
];

function getTimeframeSince(tf: Timeframe): number {
  const now = Math.floor(Date.now() / 1000);
  switch (tf) {
    case '24h': return now - 86400;
    case '7d': return now - 7 * 86400;
    case '1m': return now - 30 * 86400;
    case 'all': return 0;
  }
}

interface EventFeedProps {
  events: V2GridSeatEvent[];
  filterTypes: string[];
  pageSize?: number;
  showTimeframeFilter?: boolean;
  sinceTimestamp?: number;
  taxToken?: string;
  taxTokenDecimals?: number;
  /** Raw indexer value — prefer this for `getTaxTokenConfig` (same as `boardroom/page.tsx`). */
  taxTokenDecimalsIndexer?: number;
  takeoverPriceUsd?: number;
}

const PAGE_SIZE = 10;

const EXPLORER_BASE =
  process.env.NEXT_PUBLIC_CHAIN_ID === '84532'
    ? 'https://sepolia.basescan.org'
    : 'https://basescan.org';

export function EventFeed({ events, filterTypes, pageSize = PAGE_SIZE, showTimeframeFilter, sinceTimestamp, taxToken, taxTokenDecimals, taxTokenDecimalsIndexer, takeoverPriceUsd = 0 }: EventFeedProps) {
  const ttc = getTaxTokenConfig(taxToken, undefined, taxTokenDecimalsIndexer ?? taxTokenDecimals);
  const [timeframe, setTimeframe] = useState<Timeframe>('all');
  const [displayCount, setDisplayCount] = useState(pageSize);
  const typesSet = useMemo(() => new Set(filterTypes), [filterTypes]);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const allFiltered = useMemo(() => {
    let since = 0;
    if (sinceTimestamp != null) {
      since = sinceTimestamp;
    } else if (showTimeframeFilter) {
      since = getTimeframeSince(timeframe);
    }
    return events
      .filter(e => typesSet.has(e.eventType) && e.timestamp >= since)
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [events, typesSet, timeframe, showTimeframeFilter, sinceTimestamp]);

  const displayed = allFiltered.slice(0, displayCount);
  const hasMore = displayCount < allFiltered.length;

  const loadMore = useCallback(() => {
    if (hasMore) setDisplayCount(c => c + pageSize);
  }, [hasMore, pageSize]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '100px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const batchIdentities = useUserIdentities(displayed);

  return (
    <div>
      {showTimeframeFilter && sinceTimestamp == null && (
        <div className="flex items-center justify-end mb-0.5">
          <div className="flex gap-0.5">
            {TIMEFRAME_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setTimeframe(opt.value); setDisplayCount(pageSize); }}
                className={cn(
                  'text-[8px] font-mono uppercase tracking-[0.12em] px-1 py-0.5 rounded-[2px] transition-colors',
                  timeframe === opt.value
                    ? 'bg-amber-500/15 text-amber-100 border border-amber-400/30'
                    : 'text-zinc-500 hover:text-zinc-200 border border-transparent',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {displayed.length === 0 ? (
        <div className="flex items-center justify-center py-3 text-[10px] font-mono text-zinc-500">
          No activity yet
        </div>
      ) : (
        <div className="max-h-[148px] space-y-0 overflow-y-auto pr-0.5">
          {displayed.map(ev => (
            <EventRow key={ev.id} event={ev} batchIdentities={batchIdentities} ttc={ttc} takeoverPriceUsd={takeoverPriceUsd} />
          ))}
          {hasMore && <div ref={sentinelRef} className="h-1" />}
        </div>
      )}
    </div>
  );
}

function EventRow({ event, batchIdentities, ttc, takeoverPriceUsd = 0 }: { event: V2GridSeatEvent; batchIdentities: Record<string, IdentityInfo>; ttc: ReturnType<typeof getTaxTokenConfig>; takeoverPriceUsd?: number }) {
  const { label, color, detail, priceDirection } = getEventDisplay(event, ttc, takeoverPriceUsd);
  const hasOldHolder = (event.eventType === 'BOUGHT_OUT' || event.eventType === 'BOUGHT_FROM_CREATOR') && event.oldHolder;

  return (
    <div
      className="grid items-center gap-1 px-0.5 py-0.5 text-[10px] leading-tight text-zinc-300 hover:bg-white/[0.04] rounded-[2px] transition-colors"
      style={{ gridTemplateColumns: '10px 18px minmax(0,1fr) 18px minmax(0,1.1fr) auto' }}
    >
      {/* Indicator dot / caret */}
      <div className="flex items-center justify-center">
        {priceDirection ? (
          priceDirection === 'up' ? (
            <ChevronUp className="h-2.5 w-2.5 text-emerald-400/90" />
          ) : (
            <ChevronDown className="h-2.5 w-2.5 text-red-400/90" />
          )
        ) : (
          <span className={cn('h-1 w-1 rounded-full', color)} />
        )}
      </div>
      {/* Buyer — instant blocky thumb; identity in tooltip */}
      <div className="flex items-center justify-center">
        <AvatarTooltip
          address={event.user}
          identity={event.userIdentity}
          batchIdentities={batchIdentities}
          greyed={event.eventType === 'FORFEITED'}
          triggerSize="xxs"
          instantThumbnail
        />
      </div>
      {/* Action label */}
      <span className="min-w-0 truncate font-mono text-[9px] uppercase tracking-[0.06em] text-zinc-500">
        {label}
      </span>
      {/* Seller */}
      <div className="flex items-center justify-center">
        {hasOldHolder ? (
          <AvatarTooltip
            address={event.oldHolder!}
            identity={event.oldHolderIdentity}
            batchIdentities={batchIdentities}
            greyed
            triggerSize="xxs"
            instantThumbnail
          />
        ) : (
          <span className="inline-block h-[18px] w-[18px]" aria-hidden />
        )}
      </div>
      {/* Price */}
      <span className="min-w-0 truncate text-right font-mono text-[10px] tabular-nums text-amber-100/90">
        {detail || ''}
      </span>
      {/* Time */}
      {event.txHash ? (
        <a
          href={`${EXPLORER_BASE}/tx/${event.txHash}`}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-right font-mono text-[9px] text-zinc-500 hover:text-amber-100/80 whitespace-nowrap transition-colors"
        >
          {formatTimeAgo(event.timestamp * 1000)}
        </a>
      ) : (
        <span className="shrink-0 text-right font-mono text-[9px] text-zinc-500 whitespace-nowrap">
          {formatTimeAgo(event.timestamp * 1000)}
        </span>
      )}
    </div>
  );
}

function fmtEventPrice(raw: string, ttc: ReturnType<typeof getTaxTokenConfig>, priceUsd: number): string {
  if (ttc.isTakeover) {
    // Wait for the live USD oracle before printing a TAKEOVER amount —
    // the prior fallback (`formatTaxAmount` → e.g. `"2342334.00 TAKEOVER"`)
    // flashed raw token values into the activity feed during the brief
    // window between SSE delivery and the TAKEOVER price refresh tick.
    // A dash keeps the row legible (the timestamp + holder + event
    // label still tell the story) until USD lands.
    if (priceUsd > 0) return takeoverToUsd(BigInt(raw), priceUsd);
    return '—';
  }
  return formatTaxAmount(BigInt(raw), ttc);
}

function getEventDisplay(event: V2GridSeatEvent, ttc: ReturnType<typeof getTaxTokenConfig>, priceUsd: number): { label: string; color: string; detail?: string; priceDirection?: 'up' | 'down' } {
  switch (event.eventType) {
    case 'BOUGHT_VACANT':
      return { label: 'claimed', color: 'bg-emerald-400', detail: event.price ? `→ ${fmtEventPrice(event.price, ttc, priceUsd)}` : undefined };
    case 'BOUGHT_FROM_CREATOR':
      return { label: 'bought from creator', color: 'bg-blue-400', detail: event.price ? fmtEventPrice(event.price, ttc, priceUsd) : undefined };
    case 'BOUGHT_OUT':
      return { label: 'bought out', color: 'bg-amber-400', detail: event.price ? fmtEventPrice(event.price, ttc, priceUsd) : undefined };
    case 'FORFEITED':
      return { label: 'forfeited', color: 'bg-red-400' };
    case 'PRICE_UPDATED': {
      const oldP = event.price ? Number(event.price) : 0;
      const newP = event.newPrice ? Number(event.newPrice) : 0;
      const direction: 'up' | 'down' = newP >= oldP ? 'up' : 'down';
      return {
        label: 'reprice',
        color: direction === 'up' ? 'text-emerald-400' : 'text-red-400',
        detail: event.price && event.newPrice
          ? `${fmtEventPrice(event.price, ttc, priceUsd)} → ${fmtEventPrice(event.newPrice, ttc, priceUsd)}`
          : undefined,
        priceDirection: direction,
      };
    }
    default:
      return { label: event.eventType.toLowerCase().replace(/_/g, ' '), color: 'bg-muted-foreground' };
  }
}
